import { DateTime } from 'luxon';
import {
  ModelRecord,
  StoreModel,
  ClinicVeterinarianModel,
  ClinicUserModel,
  IClinicUserModel,
  IVisitListing,
  VisitListingModel,
  IReservationModel,
  PhotoModel,
  ReservationModel,
} from '@common/models';
import { DateTimeUtils } from '@common/utils';
import { computed, makeObservable, observable } from 'mobx';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';

/**
 * FROM: ClinicModel.cs
 */
export interface IClinicModel extends ISyncModel {
  clinicId: number;
  storeId: number;
  scheduledStartDate: string;
  scheduledEndDate: string;
  startDate?: string | undefined;
  endDate?: string | null;
  canOpen: boolean;
  canJoin: boolean;
  isPendingClose: boolean;
  allowChangingSingleVisitVet: boolean;
  veterinarianOverrideName: string;
  veterinarianOverrideFirstName: string;
  veterinarianOverrideLastName: string;
  veterinarianOverrideLicense: string;
  veterinarianOverrideEmail: string;
  veterinarianOverrideExpirationDate?: string | undefined;
  veterinarianName: string;
  veterinarianFirstName: string;
  veterinarianLastName: string;
  veterinarianLicense: string;
  veterinarianLicenseExpirationDate?: string | undefined;
  veterinarianEmail: string;
  defaultRabiesSerialNumber: string;
  store: StoreModel | undefined;
  veterinarians: ClinicVeterinarianModel[] | undefined;
  users: IClinicUserModel[] | undefined;
  visits: IVisitListing[]; // VisitListingModel[];
  reservations: IReservationModel[];
  serverRootUrl: string | undefined;
  isCancelled: boolean;
  isPublished: boolean;
  targetAOV: number | undefined;
  clinicAOV: number | undefined;
  wasAOVTargetMet: boolean;
  veterinarianSignaturePhotoId: number | undefined;
  saveVetSignatureToClinic: boolean;
  veterinarianSignaturePhoto: PhotoModel | undefined;
  slotDuration: number;
  appointmentCapacity: number;
  cancelledDate?: string | undefined;
  clinicCancellationReasonId?: number | undefined;
  clinicCancelledBy: string;
  clinicCancelledApprovedBy: string;
  clinicCancellationNotes: string;
  clinicCancellationProcessedDate?: string | undefined;
  businessLineId: number;
  dispatchPcc: number;
}

export class ClinicModel
  extends SyncModel
  implements ModelRecord<IClinicModel>
{
  @observable clinicId: number;
  storeId: number;
  @observable scheduledStartDate: DateTime;
  @observable scheduledEndDate: DateTime;
  @observable startTime: string;
  @observable endTime: string;
  @observable startDate: DateTime;
  @observable endDate: DateTime | null;
  canJoin: boolean;
  canOpen: boolean;
  @observable isPendingClose: boolean;
  @observable veterinarianName: string;
  @observable veterinarianFirstName: string;
  @observable veterinarianLastName: string;
  @observable veterinarianLicense: string;
  @observable veterinarianLicenseExpirationDate: DateTime | undefined;
  veterinarianEmail: string;
  store: StoreModel | undefined;
  @observable storeName: string;
  @observable storeNumber: string;
  @observable storeAddress: string;
  @observable storeCity: string;
  @observable stateAbbr: string;
  @observable clinicAddress: string;
  @observable isCancelled: boolean;
  veterinarians: ClinicVeterinarianModel[] | undefined;
  users: ClinicUserModel[] | undefined;
  serverRootUrl: string | undefined;
  @observable visits: VisitListingModel[];
  reservations: ReservationModel[];
  isPublished: boolean;
  saveVetSignatureToClinic: boolean;
  slotDuration: number;
  appointmentCapacity: number;
  cancelledDate: string | undefined;
  clinicCancellationReasonId: number | undefined;
  clinicCancelledBy: string | undefined;
  clinicCancelledApprovedBy: string | undefined;
  clinicCancellationNotes: string | undefined;
  clinicCancellationProcessedDate: string | undefined;
  businessLineId: number;

  allowChangingSingleVisitVet: boolean;
  veterinarianOverrideName: string | undefined;
  veterinarianOverrideFirstName: string | undefined;
  veterinarianOverrideLastName: string | undefined;
  veterinarianOverrideLicense: string | undefined;
  veterinarianOverrideEmail: string | undefined;
  veterinarianOverrideExpirationDate: string | undefined;
  defaultRabiesSerialNumber: string | undefined;

  targetAOV: number;
  clinicAOV: number;
  wasAOVTargetMet: boolean;
  veterinarianSignaturePhotoId: number | undefined;
  veterinarianSignaturePhoto: PhotoModel | undefined;
  dispatchPcc: number;

  /** Scheduled Appointments candidates (includes hidden; view applies showHidden filter). */
  @computed get scheduledReservations(): ReservationModel[] {
    return this.reservations.filter((r) => {
      if (r.hasVisit) return false;
      const isScheduled = !r.preVisitSMSResponse;
      const isCancelledArrived =
        !!r.preVisitSMSResponse && r.cancellationDate != null;
      void r.isHidden; // track for MobX invalidation
      return isScheduled || isCancelledArrived;
    });
  }

  constructor(params: Partial<IClinicModel>) {
    super(params);
    this.clinicId = _.defaultTo(params.clinicId, 0);
    this.storeId = _.defaultTo(params.storeId, 0);
    this.scheduledStartDate = DateTime.fromISO(
      _.defaultTo(params.scheduledStartDate, '2000-01-01'),
      { zone: 'utc' },
    );
    this.scheduledEndDate = DateTime.fromISO(
      _.defaultTo(params.scheduledEndDate, '2000-01-01'),
      { zone: 'utc' },
    );
    this.startTime = DateTimeUtils.getTimehour12(this.scheduledStartDate);
    this.endTime = DateTimeUtils.getTimehour12(this.scheduledEndDate);
    this.startDate = DateTime.fromISO(
      _.defaultTo(params.startDate, '2000-01-01'),
      { zone: 'utc' },
    );
    this.endDate = params.endDate
      ? DateTime.fromISO(params.endDate, { zone: 'utc' })
      : null;
    this.canJoin = _.defaultTo(params.canJoin, false);
    this.isPendingClose = _.defaultTo(params.isPendingClose, false);
    this.canOpen = _.defaultTo(params.canOpen, false);
    this.veterinarianName = _.defaultTo(params.veterinarianName, '');
    this.veterinarianFirstName = _.defaultTo(params.veterinarianFirstName, '');
    this.veterinarianLastName = _.defaultTo(params.veterinarianLastName, '');
    this.veterinarianLicense = _.defaultTo(params.veterinarianLicense, '');
    this.veterinarianEmail = _.defaultTo(params.veterinarianEmail, '');
    this.veterinarianLicenseExpirationDate =
      params.veterinarianLicenseExpirationDate
        ? DateTime.fromISO(params.veterinarianLicenseExpirationDate, {
            zone: 'utc',
          })
        : undefined;
    this.store = params.store ? new StoreModel(params.store) : undefined;
    this.storeName = _.defaultTo(this.store?.name?.split(' ')[0], '');
    this.storeNumber = _.defaultTo(this.store?.name?.split(' ')[1], '');
    this.storeAddress = _.defaultTo(this.store?.address, '');
    this.storeCity = _.defaultTo(this.store?.city, '');
    this.stateAbbr = _.defaultTo(this.store?.state?.abbreviation, '');
    this.clinicAddress = _.defaultTo(
      this.store?.address +
        ', ' +
        this.storeCity +
        ', ' +
        this.stateAbbr +
        ' ' +
        this.store?.zipCode,
      '',
    );
    this.isCancelled = _.defaultTo(params.isCancelled, false);
    this.veterinarians = params?.veterinarians
      ? params?.veterinarians.map((p) => new ClinicVeterinarianModel(p))
      : ([] as ClinicVeterinarianModel[]);
    this.users = params?.users
      ? params?.users.map((p) => new ClinicUserModel(p))
      : ([] as ClinicUserModel[]);
    this.serverRootUrl = _.defaultTo(params.serverRootUrl, '');

    this.visits = params?.visits
      ? params?.visits.map((v) => new VisitListingModel(v))
      : ([] as VisitListingModel[]);
    this.reservations =
      params.reservations?.map((r) => new ReservationModel(r)) ?? [];
    this.isPublished = _.defaultTo(params.isPublished, false);
    this.saveVetSignatureToClinic = _.defaultTo(
      params.saveVetSignatureToClinic,
      false,
    );
    this.slotDuration = _.defaultTo(params.slotDuration, 0);
    this.appointmentCapacity = _.defaultTo(params.appointmentCapacity, 0);
    this.cancelledDate = _.defaultTo(params.cancelledDate, undefined);
    this.clinicCancellationReasonId = _.defaultTo(
      params.clinicCancellationReasonId,
      undefined,
    );
    this.clinicCancelledBy = _.defaultTo(params.clinicCancelledBy, undefined);
    this.clinicCancelledApprovedBy = _.defaultTo(
      params.clinicCancelledApprovedBy,
      undefined,
    );
    this.clinicCancellationNotes = _.defaultTo(
      params.clinicCancellationNotes,
      undefined,
    );
    this.clinicCancellationProcessedDate = _.defaultTo(
      params.clinicCancellationProcessedDate,
      undefined,
    );
    this.businessLineId = _.defaultTo(params.businessLineId, 0);

    this.allowChangingSingleVisitVet = _.defaultTo(
      params.allowChangingSingleVisitVet,
      false,
    );
    this.veterinarianOverrideName = _.defaultTo(
      params.veterinarianOverrideName,
      undefined,
    );
    this.veterinarianOverrideFirstName = _.defaultTo(
      params.veterinarianOverrideFirstName,
      undefined,
    );
    this.veterinarianOverrideLastName = _.defaultTo(
      params.veterinarianOverrideLastName,
      undefined,
    );
    this.veterinarianOverrideLicense = _.defaultTo(
      params.veterinarianOverrideLicense,
      undefined,
    );
    this.veterinarianOverrideEmail = _.defaultTo(
      params.veterinarianOverrideEmail,
      undefined,
    );
    this.veterinarianOverrideExpirationDate = _.defaultTo(
      params.veterinarianOverrideExpirationDate,
      undefined,
    );
    this.defaultRabiesSerialNumber = _.defaultTo(
      params.defaultRabiesSerialNumber,
      undefined,
    );

    this.targetAOV = _.defaultTo(params.targetAOV, 0);
    this.clinicAOV = _.defaultTo(params.clinicAOV, 0);
    this.wasAOVTargetMet = _.defaultTo(params.wasAOVTargetMet, false);
    this.veterinarianSignaturePhotoId = _.defaultTo(
      params.veterinarianSignaturePhotoId,
      undefined,
    );
    this.veterinarianSignaturePhoto = params.veterinarianSignaturePhoto
      ? new PhotoModel(params.veterinarianSignaturePhoto)
      : undefined;
    this.dispatchPcc = _.defaultTo(params.dispatchPcc, 0);

    makeObservable(this);
  }
}
